import React, { useEffect, useRef } from "react";

/** Muted neon so page content stays readable — Tron-inspired arena (no PNG robots) */
const COL = {
  grid: "rgba(56, 189, 248, 0.12)",
  gridAccent: "rgba(244, 63, 94, 0.08)",
  ring: "rgba(45, 212, 191, 0.28)",
  ringDim: "rgba(45, 212, 191, 0.1)",
  red: "rgba(248, 113, 113, 0.55)",
  redGlow: "rgba(248, 113, 113, 0.35)",
  blue: "rgba(96, 165, 250, 0.58)",
  blueGlow: "rgba(96, 165, 250, 0.38)",
  sparkCore: "rgba(255, 255, 255, 0.85)",
};

const TRAIL_LEN = 48;
const MAX_SPARKS = 96;
/** Drawing scale (collision radius set separately in resize) */
const BOT_VIS_SCALE = 3.15;
const CHARGE_INTERVAL = 2.75;
const CHARGE_BOOST = 160;
const CLASH_SLAP = 285;
const SPEED_CAP = 440;

function clamp(v, lo, hi) {
  return Math.min(hi, Math.max(lo, v));
}

function distSqTrail(prev, x, y) {
  const dx = prev.x - x;
  const dy = prev.y - y;
  return dx * dx + dy * dy;
}

/** Regular octagon: flat top / flat bottom (vertex circumradius `circumR`). */
function octagonVerts(cx, cy, circumR) {
  const verts = [];
  for (let i = 0; i < 8; i++) {
    const ang = -Math.PI / 2 + Math.PI / 8 + (i * Math.PI) / 4;
    verts.push({ x: cx + circumR * Math.cos(ang), y: cy + circumR * Math.sin(ang) });
  }
  return verts;
}

function strokeOctagonPath(ctx, verts) {
  ctx.beginPath();
  ctx.moveTo(verts[0].x, verts[0].y);
  for (let i = 1; i < 8; i++) ctx.lineTo(verts[i].x, verts[i].y);
  ctx.closePath();
  ctx.stroke();
}

/** Circle vs convex octagon edges: push out + reflect velocity off edge normal. */
function resolveWallOctagon(x, y, vx, vy, r, verts, passes = 4) {
  let px = x;
  let py = y;
  let nvx = vx;
  let nvy = vy;

  for (let p = 0; p < passes; p++) {
    for (let i = 0; i < 8; i++) {
      const a = verts[i];
      const b = verts[(i + 1) % 8];
      const abx = b.x - a.x;
      const aby = b.y - a.y;
      const abLenSq = abx * abx + aby * aby || 1e-6;
      const t = clamp(((px - a.x) * abx + (py - a.y) * aby) / abLenSq, 0, 1);
      const qx = a.x + t * abx;
      const qy = a.y + t * aby;
      let ddx = px - qx;
      let ddy = py - qy;
      let d = Math.hypot(ddx, ddy);
      if (d < 1e-5) {
        ddx = -(b.y - a.y);
        ddy = b.x - a.x;
        d = Math.hypot(ddx, ddy) || 1e-6;
      }
      if (d >= r) continue;
      const pen = r - d;
      const nx = ddx / d;
      const ny = ddy / d;
      px += nx * pen;
      py += ny * pen;
      const wallOutX = -nx;
      const wallOutY = -ny;
      const vDot = nvx * wallOutX + nvy * wallOutY;
      if (vDot > 0) {
        nvx -= 2 * vDot * wallOutX;
        nvy -= 2 * vDot * wallOutY;
      }
    }
  }
  return { x: px, y: py, vx: nvx, vy: nvy };
}

/**
 * Equal-mass disks: positional separation + 1D elastic response along contact normal.
 * Ignores tangential friction (reasonable for icy arena).
 */
function resolveBotCollision(a, b) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const d = Math.hypot(dx, dy) || 1e-6;
  const nx = dx / d;
  const ny = dy / d;
  const overlap = a.r + b.r - d;
  if (overlap <= 0) return { hit: false };

  const sep = overlap * 0.58;
  a.x -= nx * sep;
  a.y -= ny * sep;
  b.x += nx * sep;
  b.y += ny * sep;

  const relNx = (b.vx - a.vx) * nx + (b.vy - a.vy) * ny;
  if (relNx >= 0) return { hit: false };

  const restitution = 1.2;
  const impulse = -(1 + restitution) * relNx * 0.5;
  a.vx -= impulse * nx;
  a.vy -= impulse * ny;
  b.vx += impulse * nx;
  b.vy += impulse * ny;

  a.vx -= nx * CLASH_SLAP;
  a.vy -= ny * CLASH_SLAP;
  b.vx += nx * CLASH_SLAP;
  b.vy += ny * CLASH_SLAP;

  return { hit: true };
}

function clampSpeed(vx, vy, cap) {
  const sp = Math.hypot(vx, vy);
  if (sp <= cap) return { vx, vy };
  const k = cap / sp;
  return { vx: vx * k, vy: vy * k };
}

function octagonBBox(verts) {
  let minX = verts[0].x;
  let maxX = verts[0].x;
  let minY = verts[0].y;
  let maxY = verts[0].y;
  for (let i = 1; i < verts.length; i++) {
    minX = Math.min(minX, verts[i].x);
    maxX = Math.max(maxX, verts[i].x);
    minY = Math.min(minY, verts[i].y);
    maxY = Math.max(maxY, verts[i].y);
  }
  return { minX, maxX, minY, maxY };
}

/** Coarse grid clipped to octagon (large cells = "bigger grid"). */
function drawTronGrid(ctx, w, h, clipVerts) {
  const { minX, maxX, minY, maxY } = octagonBBox(clipVerts);
  const span = Math.max(maxX - minX, maxY - minY);
  const step = Math.max(40, Math.round(span / 3.2));
  const pad = step * 1.2;

  ctx.save();
  ctx.beginPath();
  ctx.moveTo(clipVerts[0].x, clipVerts[0].y);
  for (let i = 1; i < 8; i++) ctx.lineTo(clipVerts[i].x, clipVerts[i].y);
  ctx.closePath();
  ctx.clip();

  ctx.strokeStyle = COL.grid;
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (let x = Math.floor((minX - pad) / step) * step; x <= maxX + pad; x += step) {
    ctx.moveTo(x, minY - pad);
    ctx.lineTo(x, maxY + pad);
  }
  for (let y = Math.floor((minY - pad) / step) * step; y <= maxY + pad; y += step) {
    ctx.moveTo(minX - pad, y);
    ctx.lineTo(maxX + pad, y);
  }
  ctx.stroke();

  const step2 = Math.max(20, Math.round(step * 0.42));
  ctx.strokeStyle = COL.gridAccent;
  ctx.lineWidth = 0.5;
  ctx.globalAlpha = 0.35;
  ctx.beginPath();
  for (let x = Math.floor((minX - pad) / step2) * step2; x <= maxX + pad; x += step2) {
    ctx.moveTo(x, minY - pad);
    ctx.lineTo(x, maxY + pad);
  }
  for (let y = Math.floor((minY - pad) / step2) * step2; y <= maxY + pad; y += step2) {
    ctx.moveTo(minX - pad, y);
    ctx.lineTo(maxX + pad, y);
  }
  ctx.stroke();
  ctx.globalAlpha = 1;
  ctx.restore();
}

function drawArenaRing(ctx, octOuter, octInnerGlow) {
  ctx.strokeStyle = COL.ringDim;
  ctx.lineWidth = 3;
  strokeOctagonPath(ctx, octOuter);

  ctx.strokeStyle = COL.ring;
  ctx.lineWidth = 1.5;
  ctx.shadowBlur = 14;
  ctx.shadowColor = "rgba(45, 212, 191, 0.25)";
  strokeOctagonPath(ctx, octInnerGlow);
  ctx.shadowBlur = 0;
}

/** Red: wedge vertical-spinner style */
function drawBotRed(ctx, x, y, ang) {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(ang);
  ctx.scale(BOT_VIS_SCALE, BOT_VIS_SCALE);
  ctx.strokeStyle = COL.red;
  ctx.lineWidth = 2;
  ctx.shadowBlur = 10;
  ctx.shadowColor = COL.redGlow;
  ctx.beginPath();
  ctx.moveTo(14, 0);
  ctx.lineTo(-10, 11);
  ctx.lineTo(-6, 0);
  ctx.lineTo(-10, -11);
  ctx.closePath();
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(-4, 0, 7, 0, Math.PI * 2);
  ctx.stroke();
  ctx.shadowBlur = 0;
  ctx.restore();
}

/** Blue: wider hammer / lifter silhouette — clearly different read */
function drawBotBlue(ctx, x, y, ang) {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(ang);
  ctx.scale(BOT_VIS_SCALE, BOT_VIS_SCALE);
  ctx.strokeStyle = COL.blue;
  ctx.lineWidth = 2;
  ctx.shadowBlur = 10;
  ctx.shadowColor = COL.blueGlow;
  ctx.strokeRect(-12, -10, 22, 20);
  ctx.beginPath();
  ctx.moveTo(11, -7);
  ctx.lineTo(18, 0);
  ctx.lineTo(11, 8);
  ctx.moveTo(-12, -10);
  ctx.lineTo(-20, 0);
  ctx.lineTo(-12, 10);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(2, 0, 4, 0, Math.PI * 2);
  ctx.stroke();
  ctx.shadowBlur = 0;
  ctx.restore();
}

function pushTrail(trail, x, y) {
  trail.push({ x, y });
  while (trail.length > TRAIL_LEN) trail.shift();
}

/** Red bot: scarlet / orange-red only (no sky-blue component) */
function drawTrailRed(ctx, trail) {
  if (trail.length < 2) return;
  for (let i = 1; i < trail.length; i++) {
    const a = trail[i - 1];
    const b = trail[i];
    const t = i / trail.length;
    const alpha = 0.14 + t * 0.52;
    ctx.strokeStyle = `rgba(255, 45, 25, ${alpha})`;
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(b.x, b.y);
    ctx.lineWidth = 1.4 + t * 3.1;
    ctx.shadowBlur = 6 + t * 15;
    ctx.shadowColor = `rgba(255, 80, 40, ${Math.min(0.72, alpha + 0.28)})`;
    ctx.stroke();
  }
  ctx.shadowBlur = 0;
}

/** Blue bot: cool cyan-blue trail */
function drawTrailBlue(ctx, trail) {
  if (trail.length < 2) return;
  for (let i = 1; i < trail.length; i++) {
    const a = trail[i - 1];
    const b = trail[i];
    const t = i / trail.length;
    const alpha = 0.12 + t * 0.45;
    ctx.strokeStyle = `rgba(56, 189, 248, ${alpha})`;
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(b.x, b.y);
    ctx.lineWidth = 1.2 + t * 2.8;
    ctx.shadowBlur = 5 + t * 14;
    ctx.shadowColor = `rgba(96, 210, 255, ${Math.min(0.6, alpha + 0.22)})`;
    ctx.stroke();
  }
  ctx.shadowBlur = 0;
}

export default function SponsorsTronArena() {
  const canvasRef = useRef(null);
  /** Only use to tone down trails/sparks — bots always move (decorative arena must not freeze for OS “reduce motion”) */
  const reduceEffectsRef = useRef(false);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return undefined;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    reduceEffectsRef.current = mq.matches;
    const onChange = () => {
      reduceEffectsRef.current = mq.matches;
    };
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    let raf = 0;
    let last = performance.now();

    const botA = {
      x: 0,
      y: 0,
      vx: 0,
      vy: 0,
      r: 16,
      ang: 0,
      trail: [],
      style: "red",
    };
    const botB = {
      x: 0,
      y: 0,
      vx: 0,
      vy: 0,
      r: 15,
      ang: 0,
      trail: [],
      style: "blue",
    };
    const sparks = [];
    let sparkCooldown = 0;
    let botsPlaced = false;
    let chargeTimer = 0;

    function resize() {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = window.innerWidth;
      const h = window.innerHeight;
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      /* ~4× linear vs original 0.30×0.22; clamp so octagon stays on-canvas */
      const arW = Math.min(w * 0.3 * 4, w * 0.94);
      const arH = Math.min(h * 0.22 * 4, h * 0.88);
      const padL = (w - arW) * 0.5;
      const padT = (h - arH) * 0.5;
      const minDim = Math.min(arW, arH);
      botA.r = clamp(minDim * 0.175, 30, 56);
      botB.r = clamp(minDim * 0.165, 28, 54);

      const cx = padL + arW / 2;
      const cy = padT + arH / 2;
      const ringInset = 10;
      const circumOuter = Math.max(48, Math.min(arW, arH) / 2 - ringInset);
      const octOuter = octagonVerts(cx, cy, circumOuter);
      const wallPad = 6;
      const circumPlay = Math.max(
        circumOuter * 0.36,
        circumOuter - botA.r - wallPad - 4,
      );
      const octPlay = octagonVerts(cx, cy, circumPlay);
      const octRingInner = octagonVerts(cx, cy, Math.max(28, circumOuter - 5));

      if (!botsPlaced) {
        const apo = circumPlay * Math.cos(Math.PI / 8);
        const Rspawn = Math.max(28, apo - botA.r - 8);
        botA.x = cx - Rspawn * 0.78;
        botA.y = cy + arH * 0.055;
        botB.x = cx + Rspawn * 0.78;
        botB.y = cy - arH * 0.055;
        const speed = clamp(minDim * 0.064, 28, 52);
        botA.vx = speed * 0.65;
        botA.vy = speed * 0.42;
        botB.vx = -speed * 0.62;
        botB.vy = -speed * 0.48;
        botsPlaced = true;
      }

      return {
        w,
        h,
        padL,
        padT,
        arW,
        arH,
        cx,
        cy,
        octOuter,
        octPlay,
        octRingInner,
      };
    }

    let bounds = resize();

    const onResize = () => {
      bounds = resize();
    };
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(onResize) : null;
    ro?.observe(document.documentElement);
    window.addEventListener("resize", onResize);

    function spawnSparks(x, y) {
      const n = 14;
      for (let i = 0; i < n && sparks.length < MAX_SPARKS; i++) {
        const ang = Math.random() * Math.PI * 2;
        const sp = 40 + Math.random() * 120;
        sparks.push({
          x,
          y,
          vx: Math.cos(ang) * sp,
          vy: Math.sin(ang) * sp,
          life: 0,
          max: 0.35 + Math.random() * 0.25,
        });
      }
    }

    function step(now) {
      const rawDt = (now - last) / 1000;
      last = now;
      const dt = Math.max(1 / 120, Math.min(rawDt, 0.05));
      bounds = bounds || resize();

      const { w, h, octOuter, octPlay, octRingInner } = bounds;

      ctx.clearRect(0, 0, w, h);

      drawTronGrid(ctx, w, h, octOuter);
      drawArenaRing(ctx, octOuter, octRingInner);

      const lightMotion = reduceEffectsRef.current;

      chargeTimer += dt;
      if (chargeTimer >= CHARGE_INTERVAL) {
        chargeTimer = 0;
        const ddx = botB.x - botA.x;
        const ddy = botB.y - botA.y;
        const len = Math.hypot(ddx, ddy) || 1;
        const ux = ddx / len;
        const uy = ddy / len;
        botA.vx += ux * CHARGE_BOOST;
        botA.vy += uy * CHARGE_BOOST;
        botB.vx -= ux * CHARGE_BOOST;
        botB.vy -= uy * CHARGE_BOOST;
        let v = clampSpeed(botA.vx, botA.vy, SPEED_CAP);
        botA.vx = v.vx;
        botA.vy = v.vy;
        v = clampSpeed(botB.vx, botB.vy, SPEED_CAP);
        botB.vx = v.vx;
        botB.vy = v.vy;
      }

      botA.x += botA.vx * dt;
      botA.y += botA.vy * dt;
      botB.x += botB.vx * dt;
      botB.y += botB.vy * dt;

      const wA = resolveWallOctagon(botA.x, botA.y, botA.vx, botA.vy, botA.r, octPlay);
      botA.x = wA.x;
      botA.y = wA.y;
      botA.vx = wA.vx;
      botA.vy = wA.vy;

      const wB = resolveWallOctagon(botB.x, botB.y, botB.vx, botB.vy, botB.r, octPlay);
      botB.x = wB.x;
      botB.y = wB.y;
      botB.vx = wB.vx;
      botB.vy = wB.vy;

      const col = resolveBotCollision(botA, botB);
      if (col.hit && sparkCooldown <= 0) {
        if (!lightMotion) spawnSparks((botA.x + botB.x) / 2, (botA.y + botB.y) / 2);
        sparkCooldown = 0.14;
      }
      sparkCooldown = Math.max(0, sparkCooldown - dt);

      const wA2 = resolveWallOctagon(botA.x, botA.y, botA.vx, botA.vy, botA.r, octPlay);
      botA.x = wA2.x;
      botA.y = wA2.y;
      botA.vx = wA2.vx;
      botA.vy = wA2.vy;
      const wB2 = resolveWallOctagon(botB.x, botB.y, botB.vx, botB.vy, botB.r, octPlay);
      botB.x = wB2.x;
      botB.y = wB2.y;
      botB.vx = wB2.vx;
      botB.vy = wB2.vy;

      let spCap = clampSpeed(botA.vx, botA.vy, SPEED_CAP);
      botA.vx = spCap.vx;
      botA.vy = spCap.vy;
      spCap = clampSpeed(botB.vx, botB.vy, SPEED_CAP);
      botB.vx = spCap.vx;
      botB.vy = spCap.vy;

      botA.ang = Math.atan2(botA.vy, botA.vx);
      botB.ang = Math.atan2(botB.vy, botB.vx);

      if (!lightMotion) {
        pushTrail(botA.trail, botA.x, botA.y);
        pushTrail(botB.trail, botB.x, botB.y);
      } else {
        const tailA = botA.trail;
        const tailB = botB.trail;
        if (tailA.length === 0 || distSqTrail(tailA[tailA.length - 1], botA.x, botA.y) > 900) pushTrail(tailA, botA.x, botA.y);
        if (tailB.length === 0 || distSqTrail(tailB[tailB.length - 1], botB.x, botB.y) > 900) pushTrail(tailB, botB.x, botB.y);
        while (tailA.length > 12) tailA.shift();
        while (tailB.length > 12) tailB.shift();
      }

      for (let i = sparks.length - 1; i >= 0; i--) {
        const s = sparks[i];
        s.life += dt;
        s.x += s.vx * dt;
        s.y += s.vy * dt;
        s.vx *= 0.92;
        s.vy *= 0.92;
        if (s.life > s.max) sparks.splice(i, 1);
      }

      drawTrailRed(ctx, botA.trail);
      drawTrailBlue(ctx, botB.trail);

      for (const s of sparks) {
        const t = 1 - s.life / s.max;
        ctx.fillStyle = `rgba(255, 255, 255, ${0.15 + t * 0.65})`;
        ctx.shadowBlur = 6 * t;
        ctx.shadowColor = COL.sparkCore;
        ctx.beginPath();
        ctx.arc(s.x, s.y, 1.2 + t * 1.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;
      }

      drawBotRed(ctx, botA.x, botA.y, botA.ang);
      drawBotBlue(ctx, botB.x, botB.y, botB.ang);

      raf = requestAnimationFrame(step);
    }

    raf = requestAnimationFrame(step);

    return () => {
      cancelAnimationFrame(raf);
      ro?.disconnect();
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return <canvas ref={canvasRef} className="sponsors-tron-canvas" aria-hidden="true" />;
}
